/**
 * Dong (아파트 동) ObjectType API wrapper.
 *
 * Parallel to `zoneApi.ensureZoneObjectType`: idempotent fetch-or-create of the
 * project-scoped 동 ObjectType (single instance per project). Auto-imported 동
 * labels (e.g. `101동`) reference this type's id so they appear as a real,
 * selectable object type in the editor and export under the `dongs` layer.
 *
 * Unlike zones, 동 objects are plain text labels (no polygon) — see
 * `createDongLabelCell`. This module only owns the ObjectType lifecycle.
 */

import type { APIResponse } from './types';
import type {
  CreateObjectTypeRequest,
  ObjectTypeResponse,
} from './types/layerMapping';
import { objectTypeApi } from './services/layerMappingService';

/** Display name of the 동 ObjectType (Korean, per project convention). */
export const DONG_OBJECT_TYPE_NAME = '동';
/** 동 label text color (#hex). Near-black for legibility on light drawings. */
export const DONG_DEFAULT_COLOR = '#111827';
/**
 * `data.layer` value for 동 cells. Plural lowercase so export buckets them under
 * a `dongs` group, consistent with zones' `zones` layer naming.
 */
export const DONG_LAYER_NAME = 'dongs';
/** Render priority — above zones (50) so building labels sit on top. */
export const DONG_PRIORITY = 60;

interface ListObjectTypesEnvelope {
  data?: ObjectTypeResponse[] | { types?: ObjectTypeResponse[] };
}

function extractObjectTypes(
  response: APIResponse<unknown>
): ObjectTypeResponse[] {
  const data = (response as ListObjectTypesEnvelope).data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray(data.types)) {
    return data.types;
  }
  return [];
}

export const dongApi = {
  /**
   * Fetch the 동 ObjectType for a project, creating it if absent.
   *
   * Race handling mirrors `ensureZoneObjectType`: on a lost create race
   * (backend enforces unique name per project) re-read the list once.
   */
  async ensureDongObjectType(projectId: number): Promise<ObjectTypeResponse> {
    const listResp = await objectTypeApi.getList(projectId);
    const current = extractObjectTypes(listResp).find(
      (t) => t.name === DONG_OBJECT_TYPE_NAME
    );
    if (current) return current;

    const req: CreateObjectTypeRequest = {
      name: DONG_OBJECT_TYPE_NAME,
      color: DONG_DEFAULT_COLOR,
      priority: DONG_PRIORITY,
      properties: [],
    };

    try {
      const createResp = await objectTypeApi.create(projectId, req);
      if (!createResp.data) {
        throw new Error('동 ObjectType create returned no data');
      }
      return createResp.data;
    } catch (error: unknown) {
      const retryResp = await objectTypeApi.getList(projectId);
      const afterRace = extractObjectTypes(retryResp).find(
        (t) => t.name === DONG_OBJECT_TYPE_NAME
      );
      if (afterRace) return afterRace;
      throw error;
    }
  },
};

export default dongApi;
